import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { visibleInterval } from "@/lib/poll";
import { useNavigate } from "@/lib/useNav";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playPing } from "@/lib/chatSound";
import { showDesktop } from "@/lib/desktopNotify";
import { pushOnHere, refreshPush } from "@/lib/webpush";

const ChatContext = createContext(null);
const MAX_WINDOWS = 4;

export function ChatProvider({ children }) {
  const { user, patchPreferences } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState({ total: 0, per_chat: {} });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [windows, setWindows] = useState([]); // [{ id, name, minimized }]
  const [tick, setTick] = useState(0);
  const lastIncoming = useRef(undefined);

  const refreshUnread = useCallback(async () => {
    try {
      const { data } = await client.get("/chats/unread/count");
      setUnread({ total: data.total || 0, per_chat: data.per_chat || {} });
      const li = data.latest_incoming_at || null;
      if (lastIncoming.current === undefined) {
        lastIncoming.current = li;
      } else if (li && li !== lastIncoming.current) {
        const isNewer = lastIncoming.current === null || li > lastIncoming.current;
        lastIncoming.current = li;
        if (isNewer) {
          if (user?.preferences?.chat_sound !== false) playPing();
          if (user?.preferences?.chat_desktop === true && data.latest_incoming && !pushOnHere()) {
            const li2 = data.latest_incoming;
            const title = li2.chat_name && li2.chat_name !== li2.sender_name
              ? `${li2.sender_name} · ${li2.chat_name}`
              : `New message from ${li2.sender_name}`;
            showDesktop({
              title,
              body: li2.text || "New message",
              tag: `chat-${li2.chat_id}`,
              url: `/chat?c=${li2.chat_id}`,
              onClick: () => navigate(`/chat?c=${li2.chat_id}`),
            });
          }
        }
      }
    } catch (e) { /* ignore */ }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.id) refreshPush();
  }, [user?.id]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onMsg = (e) => { if (e.data?.type === "dreamcrm:navigate" && e.data.url) navigate(e.data.url); };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    refreshUnread();
    return visibleInterval(refreshUnread, 10000);
  }, [user, refreshUnread]);

  const setSound = async (on) => {
    patchPreferences({ chat_sound: on });
    try { await client.put("/me/preferences", { preferences: { chat_sound: on } }); } catch (e) { /* ignore */ }
  };

  const openWindow = useCallback((id, name) => {
    setWindows((ws) => {
      if (ws.find((w) => w.id === id)) return ws.map((w) => (w.id === id ? { ...w, minimized: false } : w));
      return [...ws, { id, name: name || "Chat", minimized: false }].slice(-MAX_WINDOWS);
    });
  }, []);
  const closeWindow = useCallback((id) => setWindows((ws) => ws.filter((w) => w.id !== id)), []);
  const minimizeWindow = useCallback((id, val) => setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, minimized: val ?? !w.minimized } : w))), []);

  const bump = useCallback(() => { setTick((t) => t + 1); refreshUnread(); }, [refreshUnread]);

  return (
    <ChatContext.Provider value={{
      unread, refreshUnread, drawerOpen, setDrawerOpen,
      windows, openWindow, openChat: openWindow, closeWindow, minimizeWindow,
      tick, bump, soundOn: user?.preferences?.chat_sound !== false, setSound,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
